import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { AuthService, type AuthUser } from './auth'

// ---- Event bus (same pattern as adminSchoolContext.ts) ----

let _announcementCount = 0

export const getAnnouncementCount = (): number => _announcementCount

export const setAnnouncementCount = (count: number): void => {
  _announcementCount = count
  window.dispatchEvent(new CustomEvent('announcementCountChanged', { detail: count }))
}

export const useAnnouncementCount = (): number => {
  const [count, setCount] = useState<number>(_announcementCount)
  useEffect(() => {
    const handler = (e: Event) => setCount((e as CustomEvent<number>).detail)
    window.addEventListener('announcementCountChanged', handler)
    return () => window.removeEventListener('announcementCountChanged', handler)
  }, [])
  return count
}

// ---- Service ----

export class AnnouncementReadsService {
  static async getLastReadAt(): Promise<string | null> {
    const user = await AuthService.getAuthUser()
    if (!user) return null
    const { data } = await supabase
      .from('announcement_reads')
      .select('last_read_at')
      .eq('user_id', user.id)
      .maybeSingle()
    return data?.last_read_at ?? null
  }

  // Count unread announcements (system + school, grade/class filtered).
  // Group targeting is skipped here for performance — the page is the authoritative view.
  static async getUnreadCount(currentUser: AuthUser): Promise<number> {
    const lastReadAt = await this.getLastReadAt()
    const now = new Date().toISOString()

    let systemQuery = supabase
      .from('announcements')
      .select('*', { count: 'exact', head: true })
      .lte('published_at', now)
    if (lastReadAt) systemQuery = systemQuery.gt('published_at', lastReadAt)

    let schoolQuery = currentUser.school_id
      ? supabase
          .from('school_announcements')
          .select('target_grades, target_classes')
          .eq('school_id', currentUser.school_id)
          .lte('published_at', now)
      : null
    if (schoolQuery && lastReadAt) schoolQuery = schoolQuery.gt('published_at', lastReadAt)

    const [systemResult, schoolResult] = await Promise.all([
      systemQuery,
      schoolQuery ?? Promise.resolve({ data: null, error: null }),
    ])

    if (systemResult.error) throw systemResult.error

    let schoolCount = 0
    if (schoolResult.data) {
      schoolCount = schoolResult.data.filter(ann => {
        const gradeOk =
          ann.target_grades.length === 0 ||
          (currentUser.grade != null && ann.target_grades.includes(currentUser.grade))
        const classOk =
          ann.target_classes.length === 0 ||
          (currentUser.class != null && ann.target_classes.includes(currentUser.class))
        return gradeOk && classOk
      }).length
    }

    return (systemResult.count ?? 0) + schoolCount
  }

  // Upsert last_read_at = NOW() and push count=0 to the event bus.
  static async markAllRead(): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return
    const { error } = await supabase
      .from('announcement_reads')
      .upsert({ user_id: user.id, last_read_at: new Date().toISOString() }, { onConflict: 'user_id' })
    if (error) throw error
    setAnnouncementCount(0)
  }
}
